import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Paths } from 'type-fest';
import type {
  ApolloQueryResult,
  LazyQueryResult,
  MaybeMasked,
  OperationVariables,
  QueryResult,
  SubscribeToMoreOptions,
  TypedDocumentNode,
} from '@apollo/client';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useSidebarOpenState } from '../app/layout/SidebarOpenStateContext';
import { IconButton } from '../components/Button';
import PopOverPrimitive from '../components/PopOverPrimitive';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import notificationsIcon from '../icons/non-prefilled/16x16/notifications.svg';
import { isFeatureEnabled } from '../utils/features';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import * as styles from './NotificationButton.module.scss';
import { groupNotifications } from './NotificationContent';
import NotificationDot from './NotificationDot';
import NotificationPopOver from './NotificationPopOver';

// TODO: check if status is still needed in this fragment
// eslint-disable-next-line slab-graphql/no-unused-fields
const NOTIFICATION_FRAGMENT = gql(`
  fragment NotificationButton_notification on Notification {
    id
    status
    ... on CommentNotification {
      comment {
        id
      }
    }
    ... on HistoryNotification {
      history {
        id
      }
    }
    ... on InvitationNotification {
      invitation {
        id
        acceptedUser {
          id
        }
      }
    }
    ... on PostNotification {
      post {
        id
      }
    }
    ... on ReactionNotification {
      reaction {
        id
      }
    }
    ...NotificationPopOver_notifications
  }
`);

type NotificationFragment = DocumentType<typeof NOTIFICATION_FRAGMENT>;

const UNREAD_NOTIFICATIONS_QUERY = gql(/** @complexity 28 */ `
  query NotificationButtonUnreadNotifications(
    $afterCursor: String
    $first: Int = 0
  ) {
    unreadNotifications(first: $first, after: $afterCursor) {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          ...NotificationButton_notification
        }
      }
      totalCount
    }
  }
`);

const ALL_NOTIFICATIONS_QUERY = gql(/** @complexity 28 */ `
  query notifications($afterCursor: String, $first: Int = 0) {
    notifications(first: $first, after: $afterCursor) {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          ...NotificationButton_notification
        }
      }
    }
  }
`);

const NOTIFICATION_CREATED_SUBSCRIPTION = gql(/** @complexity 56 */ `
  subscription notificationCreated {
    notification: notificationCreated {
      ...NotificationButton_notification
    }
  }
`);

const NOTIFICATIONS_UPDATED_SUBSCRIPTION = gql(/** @complexity 8 */ `
  subscription notificationsUpdated {
    notificationsUpdated {
      id
    }
  }
`);

const isNotificationsV2 = isFeatureEnabled('notifications_v2');

const filterNotifications = (
  nodes: (NotificationFragment | null | undefined)[] = [],
) =>
  nodes.filter<NotificationFragment>(
    (n): n is NotificationFragment =>
      n != null &&
      // Filters notifications for deleted items
      ((n.__typename === 'CommentNotification' && !!n.comment) ||
        (n.__typename === 'HistoryNotification' &&
          !!n.history &&
          !isNotificationsV2) ||
        (n.__typename === 'PostNotification' && !!n.post) ||
        (n.__typename === 'ReactionNotification' && !!n.reaction) ||
        (n.__typename === 'InvitationNotification' &&
          !!n.invitation?.acceptedUser &&
          !isNotificationsV2)),
  );

function useDedupedFetchMore<TData>(
  query: TypedDocumentNode<TData>,
  result:
    | QueryResult<
        TData,
        {
          first: number;
          afterCursor: null;
        }
      >
    | LazyQueryResult<TData, { first?: number | null }>,
  keyPath: Paths<TData>,
) {
  const promiseRef = useRef<Promise<
    ApolloQueryResult<MaybeMasked<TData>>
  > | null>(null);

  const fetchMore = ConnectionHelper.generateFetchMore<
    TData,
    { first?: number | null }
  >(query, result, keyPath);
  return (
    fetchMore &&
    (() => {
      if (!promiseRef.current) {
        promiseRef.current = fetchMore({ first: 10 }).then((data) => {
          promiseRef.current = null;
          return data;
        });
      }

      return promiseRef.current;
    })
  );
}

type SubscribeToMoreNotifications<
  T extends DocumentType<typeof UNREAD_NOTIFICATIONS_QUERY>,
> = (
  options: SubscribeToMoreOptions<
    T,
    OperationVariables,
    DocumentType<typeof NOTIFICATION_CREATED_SUBSCRIPTION>
  >,
) => void;

const useSubscribeToNewNotifications = <
  Key extends 'unreadNotifications' | 'notifications',
  SubscribeToMore extends Key extends 'unreadNotifications'
    ? SubscribeToMoreNotifications<
        DocumentType<typeof UNREAD_NOTIFICATIONS_QUERY>
      >
    : SubscribeToMoreNotifications<
        DocumentType<typeof ALL_NOTIFICATIONS_QUERY>
      >,
>(
  key: Key,
  subscribeToMore: SubscribeToMore,
) =>
  useEffect(
    () =>
      subscribeToMore?.({
        document: NOTIFICATION_CREATED_SUBSCRIPTION,
        updateQuery(
          prev,
          { subscriptionData: { data: { notification } = {} } },
        ) {
          const prevConnection = (() => {
            if (key === 'notifications' && 'notifications' in prev) {
              return prev.notifications;
            }
            if (
              key === 'unreadNotifications' &&
              'unreadNotifications' in prev
            ) {
              return prev.unreadNotifications;
            }
            return null;
          })();

          if (!prev || !prevConnection || !notification) {
            return prev;
          }

          return {
            ...prev,
            [key]: ConnectionHelper.insertNodeBefore(
              prevConnection,
              notification,
            ),
          };
        },
      }),
    [key, subscribeToMore],
  );

const INITIAL_NOTIFICATIONS_COUNT = 25;

const useUnreadNotificationQuery = (options: { skip?: boolean }) => {
  const result = useQuery(UNREAD_NOTIFICATIONS_QUERY, {
    variables: { first: INITIAL_NOTIFICATIONS_COUNT, afterCursor: null },
    ...options,
  });

  const {
    data: { unreadNotifications: notifications } = {},
    subscribeToMore,
    refetch,
  } = result;
  const unreadNotifications = notifications?.edges?.map((edge) => edge?.node);
  const filteredUnreadNotifications = filterNotifications(unreadNotifications);
  const invalidNotificationsCount = Math.max(
    (unreadNotifications?.length ?? 0) - filteredUnreadNotifications.length,
    0,
  );

  const initialUnreadNotifications = filteredUnreadNotifications.slice(
    0,
    INITIAL_NOTIFICATIONS_COUNT,
  );

  const initialCollapsedNotificationsCount = isNotificationsV2
    ? 0 // We don't collapse notifications in v2
    : initialUnreadNotifications.length -
      groupNotifications(initialUnreadNotifications).length;

  const totalCount =
    (notifications?.totalCount ?? 0) -
    initialCollapsedNotificationsCount -
    // In case we filtered out invalid notifications above, we need to correct the total count
    invalidNotificationsCount;

  const fetchMoreUnreadNotifications = useDedupedFetchMore(
    UNREAD_NOTIFICATIONS_QUERY,
    result,
    'unreadNotifications',
  );

  useSubscribeToNewNotifications('unreadNotifications', subscribeToMore);

  useEffect(
    () =>
      subscribeToMore({
        document: NOTIFICATIONS_UPDATED_SUBSCRIPTION,
        updateQuery: (
          prev,
          { subscriptionData: { data: { notificationsUpdated } = {} } },
        ) => {
          if (notificationsUpdated) {
            refetch({ first: INITIAL_NOTIFICATIONS_COUNT });
          }
          return prev;
        },
      }),
    [subscribeToMore, refetch, options.skip],
  );

  return {
    fetchMoreUnreadNotifications,
    unreadNotifications: filteredUnreadNotifications,
    totalCount,
  };
};

const useAllNotificationLazyQuery = () => {
  const [getAllNotifications, result] = useLazyQuery(ALL_NOTIFICATIONS_QUERY, {
    variables: { first: 10, afterCursor: null },
  });

  const { data: { notifications } = {}, subscribeToMore } = result;
  const allNotifications = filterNotifications(
    notifications?.edges?.map((edge) => edge?.node),
  );

  const fetchMoreAllNotifications = useDedupedFetchMore(
    ALL_NOTIFICATIONS_QUERY,
    result,
    'notifications',
  );

  useSubscribeToNewNotifications('notifications', subscribeToMore);

  useEffect(() => {
    if (!subscribeToMore) {
      return undefined;
    }
    return subscribeToMore({
      document: NOTIFICATIONS_UPDATED_SUBSCRIPTION,
      updateQuery: (
        prev,
        { subscriptionData: { data: { notificationsUpdated } = {} } },
      ) => {
        if (
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
          !prev ||
          !prev.notifications ||
          !notificationsUpdated
        ) {
          return prev;
        }

        const newConnection = notificationsUpdated.reduce(
          (accumulator, n) => ConnectionHelper.updateNode(accumulator, n),
          prev.notifications,
        );

        return {
          ...prev,
          notifications: newConnection,
        };
      },
    });
  }, [subscribeToMore]);

  return {
    getAllNotifications,
    fetchMoreAllNotifications,
    allNotifications,
  };
};

interface BaseNotificationButtonProps {
  unreadNotifications: NotificationFragment[];
  unreadNotificationsTotalCount: number;
  allNotifications: NotificationFragment[];
  fetchMoreUnreadNotifications: (() => void) | null;
  fetchMoreAllNotifications: (() => void) | null;
  getNotifications?: () => void;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseNotificationButton = ({
  unreadNotifications = [],
  unreadNotificationsTotalCount,
  fetchMoreUnreadNotifications,
  allNotifications = [],
  fetchMoreAllNotifications,
  getNotifications,
}: BaseNotificationButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isSidebarOpen } = useSidebarOpenState();

  if (isOpen && !isSidebarOpen) {
    setIsOpen(false);
  }

  return (
    <>
      {isOpen &&
        createPortal(<div className={styles.mobileOverlay} />, document.body)}
      <PopOverPrimitive
        trigger="click"
        placement="bottom-start"
        noFlip
        offset={4}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <PopOverPrimitive.Trigger>
          <div onMouseEnter={getNotifications}>
            <IconButton
              active={isOpen}
              className={styles.notificationButton}
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Notifications"
              size="xl"
            >
              <SVGIcon size="large">{notificationsIcon}</SVGIcon>
              {!!unreadNotificationsTotalCount && (
                <div className={styles.buttonWrapper}>
                  <NotificationDot />
                </div>
              )}
            </IconButton>
          </div>
        </PopOverPrimitive.Trigger>
        <PopOverPrimitive.Floating>
          <PopOverPrimitive.Content>
            <NotificationPopOver
              unreadNotifications={unreadNotifications}
              unreadNotificationsTotalCount={unreadNotificationsTotalCount}
              fetchMoreUnreadNotifications={fetchMoreUnreadNotifications}
              allNotifications={allNotifications}
              fetchMoreAllNotifications={fetchMoreAllNotifications}
              onNavigate={() => setIsOpen(false)}
            />
          </PopOverPrimitive.Content>
        </PopOverPrimitive.Floating>
      </PopOverPrimitive>
    </>
  );
};

const NotificationButton = () => {
  const { isSidebarOpen } = useSidebarOpenState();
  const [hasFetched, setHasFetched] = useState(false);
  const { fetchMoreUnreadNotifications, unreadNotifications, totalCount } =
    useUnreadNotificationQuery({ skip: !isSidebarOpen });

  const { getAllNotifications, allNotifications, fetchMoreAllNotifications } =
    useAllNotificationLazyQuery();

  const getNotifications = () => {
    if (hasFetched) {
      return;
    }

    getAllNotifications();
    if (fetchMoreUnreadNotifications) {
      fetchMoreUnreadNotifications();
    }

    setHasFetched(true);
  };

  return (
    <BaseNotificationButton
      fetchMoreAllNotifications={fetchMoreAllNotifications}
      unreadNotificationsTotalCount={totalCount}
      fetchMoreUnreadNotifications={fetchMoreUnreadNotifications}
      getNotifications={hasFetched ? undefined : getNotifications}
      allNotifications={allNotifications}
      unreadNotifications={unreadNotifications}
    />
  );
};

export default NotificationButton;
